import { useState } from "react";
import { Card, Gate, Head, State, useAsync } from "@authoxi/ui-shared";
import { client, directory, readSnippet } from "../api";

/**
 * The badge you can only get by earning it.
 *
 * It renders the **most recent** scan, never the best one — a badge showing an operator's
 * high-water mark keeps advertising a grade the endpoint no longer earns, which is the one way
 * a trust badge becomes actively misleading.
 *
 * That claim used to be true of the query and meaningless in practice, because nothing ever
 * produced a newer scan. The re-scan sweep does now, for hosts somebody is watching. So the
 * badge shows **when it was last checked**, right next to itself: freshness is the property
 * the whole thing rests on, and a badge that hides its own age is asking to be trusted blind.
 */
export function Badge() {
  const { data: dir } = useAsync(() => directory(10, 0));
  const [host, setHost] = useState<string | null>(null);
  const chosen = host ?? dir?.hosts[0]?.host ?? null;

  const { data: snippet, loading } = useAsync(
    async () => {
      if (!chosen) return null;
      const row = dir?.hosts.find((h) => h.host === chosen);
      return row ? await readSnippet(row.scan_id) : null;
    },
    [chosen, dir],
  );

  const row = dir?.hosts.find((h) => h.host === chosen);

  return (
    <>
      <Head
        pill="Agent-Verified"
        title="A badge you can only get by earning it."
        lede="The grade renders as an SVG you embed. It is generated live from the most recent scan — never the best one — so it stays true after you stop paying attention to it."
      />

      {!dir && loading && <State kind="loading" title="Loading…" />}

      {dir && dir.hosts.length === 0 && (
        <State
          kind="empty"
          title="Nothing has been graded yet."
          detail="A badge renders a scan. Run one first."
          action={
            <a className="btn btn-go" href="/tools/grader" style={{ marginTop: "var(--s4)" }}>
              Grade an endpoint
            </a>
          }
        />
      )}

      {dir && dir.hosts.length > 0 && (
        <>
          <Card pad="lg">
            <p className="label">Preview</p>
            <div style={{ margin: "var(--s4) 0" }} data-testid="badge-preview">
              {/* The REAL SVG the backend serves, not a CSS impression of one. A preview drawn
                  in HTML would look right and prove nothing about what people embed. */}
              {snippet && (
                <img
                  src={snippet.live_badge_url}
                  alt={`Agent-Verified: ${row?.letter ?? ""}`}
                  height={20}
                />
              )}
            </div>

            {row && (
              <p className="sm muted" data-testid="badge-age">
                Showing {row.host} — graded {row.letter} ({row.score}/100), last checked{" "}
                {row.scanned_at.slice(0, 10)}.
              </p>
            )}

            <p className="label" style={{ marginTop: "var(--s5)" }}>
              Host
            </p>
            <div className="swatches" style={{ marginTop: "var(--s3)" }}>
              {dir.hosts.slice(0, 6).map((h) => (
                <button
                  key={h.host}
                  className={`swatch ${h.host === chosen ? "on" : ""}`}
                  onClick={() => setHost(h.host)}
                >
                  {h.letter}
                </button>
              ))}
            </div>

            <div className="hr" />
            <p className="label">Embed</p>
            <pre className="code" style={{ marginTop: "var(--s3)" }} data-testid="badge-embed">
              {snippet?.markdown ?? "…"}
            </pre>
            <div className="row" style={{ marginTop: "var(--s4)" }}>
              <button
                className="btn"
                disabled={!snippet}
                onClick={() => void navigator.clipboard?.writeText(snippet?.markdown ?? "")}
              >
                Copy embed code
              </button>
              <span className="sm muted">
                Links back to the full, checkable scan — a badge that links nowhere is a sticker.
              </span>
            </div>
          </Card>

          <Gate
            client={client}
            source="badge"
            subject={chosen ?? undefined}
            title="Keep it honest automatically"
            body="The badge always shows your most recent scan. We re-check watched hosts and will email you if it ever drops a grade, so the first person to notice is you rather than a customer."
            cta="Alert me on a drop"
          />
        </>
      )}
    </>
  );
}
